(function () {
  'use strict';

  if (window.EnigmaAccountDrawerA11y) {
    window.EnigmaAccountDrawerA11y.init();
    return;
  }

  var DRAWER_SELECTOR = '[data-enigma-account-drawer]';
  var TRIGGER_SELECTOR = '.nov_btn_act[data-toggle="header_settings"]';
  var CLOSE_SELECTOR = '[nov-btn-close][data-close="header_settings"]';
  var FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  var drawer = null;
  var drawerObserver = null;
  var lastTrigger = null;
  var wasOpen = false;
  var focusGeneration = 0;
  var focusTimers = [];

  function getTriggers() {
    return Array.prototype.slice.call(document.querySelectorAll(TRIGGER_SELECTOR));
  }

  function getTitle() {
    return drawer ? drawer.querySelector('#AccountDrawerTitle') : null;
  }

  function isOpen() {
    return Boolean(drawer && drawer.classList.contains('act'));
  }

  function isConnected(element) {
    if (!element) return false;
    if (typeof element.isConnected === 'boolean') return element.isConnected;
    return Boolean(document.documentElement && document.documentElement.contains(element));
  }

  function hasBlockingState(element) {
    if (!element || element.hidden || element.disabled) return true;
    if (element.getAttribute('aria-hidden') === 'true' || element.getAttribute('aria-disabled') === 'true') return true;
    if (element.hasAttribute('inert') || element.hasAttribute('disabled')) return true;

    var blockedAncestor = typeof element.closest === 'function'
      ? element.closest('[hidden], [inert], [aria-hidden="true"]')
      : null;
    return Boolean(blockedAncestor);
  }

  function hasVisibleArea(element) {
    if (!element || typeof element.getClientRects !== 'function') return false;
    var rects = element.getClientRects();
    return Array.prototype.some.call(rects, function (rect) {
      return rect.width > 0 && rect.height > 0;
    });
  }

  function isOnScreen(element) {
    if (!isRendered(element) || typeof element.getBoundingClientRect !== 'function') return false;
    var rect = element.getBoundingClientRect();
    var viewportWidth = window.innerWidth || (document.documentElement && document.documentElement.clientWidth) || 0;
    var viewportHeight = window.innerHeight || (document.documentElement && document.documentElement.clientHeight) || 0;
    if (viewportWidth > 0 && (rect.right <= 0 || rect.left >= viewportWidth)) return false;
    if (viewportHeight > 0 && (rect.bottom <= 0 || rect.top >= viewportHeight)) return false;
    return true;
  }

  function isRendered(element) {
    if (!isConnected(element) || hasBlockingState(element)) return false;
    var styles = window.getComputedStyle(element);
    if (!styles || styles.display === 'none' || styles.visibility === 'hidden') return false;
    return hasVisibleArea(element);
  }

  function isTabbable(element) {
    if (!isRendered(element) || typeof element.focus !== 'function') return false;
    return typeof element.tabIndex === 'number' && element.tabIndex >= 0;
  }

  function getFocusableElements() {
    if (!drawer) return [];
    return Array.prototype.slice.call(drawer.querySelectorAll(FOCUSABLE_SELECTOR)).filter(isTabbable);
  }

  function focusElement(element, allowProgrammaticOnly) {
    if (!isRendered(element) || typeof element.focus !== 'function') return false;
    if (!allowProgrammaticOnly && !isTabbable(element)) return false;

    try {
      element.focus({ preventScroll: true });
    } catch (error) {
      try {
        element.focus();
      } catch (fallbackError) {
        return false;
      }
    }
    return document.activeElement === element;
  }

  function focusFirstAvailable(candidates, allowProgrammaticOnly) {
    for (var index = 0; index < candidates.length; index += 1) {
      if (focusElement(candidates[index], allowProgrammaticOnly)) return true;
    }
    return false;
  }

  function enhanceTriggers(open) {
    var title = getTitle();
    var fallbackLabel = title ? title.textContent.trim() : '';

    getTriggers().forEach(function (trigger) {
      trigger.setAttribute('aria-controls', 'AccountDrawer');
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');

      if (trigger.tagName !== 'BUTTON' && trigger.getAttribute('href') === '#') {
        trigger.setAttribute('role', 'button');
      }

      if (!trigger.getAttribute('aria-label') && fallbackLabel) {
        trigger.setAttribute('aria-label', fallbackLabel);
      }
    });
  }

  function clearFocusSchedule() {
    focusGeneration += 1;
    focusTimers.forEach(function (timer) {
      window.clearTimeout(timer);
    });
    focusTimers = [];
    return focusGeneration;
  }

  function removeFocusTimer(timer) {
    var timerIndex = focusTimers.indexOf(timer);
    if (timerIndex !== -1) focusTimers.splice(timerIndex, 1);
  }

  function focusInitialTarget(generation) {
    if (generation !== focusGeneration || !isOpen() || !drawer) return false;
    if (drawer.contains(document.activeElement) && isRendered(document.activeElement)) return true;

    var candidates = [];
    var closeButton = drawer.querySelector(CLOSE_SELECTOR);
    var focusable = getFocusableElements();
    if (closeButton && isOnScreen(closeButton)) candidates.push(closeButton);
    focusable.filter(isOnScreen).forEach(function (candidate) {
      if (candidates.indexOf(candidate) === -1) candidates.push(candidate);
    });
    focusable.forEach(function (candidate) {
      if (candidates.indexOf(candidate) === -1) candidates.push(candidate);
    });

    if (focusFirstAvailable(candidates, false)) return true;
    return focusElement(drawer, true);
  }

  function scheduleInitialFocus(generation, delay) {
    var timer = window.setTimeout(function () {
      removeFocusTimer(timer);
      focusInitialTarget(generation);
    }, delay);
    focusTimers.push(timer);
  }

  function beginInitialFocus() {
    var generation = clearFocusSchedule();
    scheduleInitialFocus(generation, 100);
    scheduleInitialFocus(generation, 200);
  }

  function isUsableTrigger(trigger) {
    if (!trigger || typeof trigger.matches !== 'function' || !trigger.matches(TRIGGER_SELECTOR)) return false;
    return isTabbable(trigger) && isOnScreen(trigger);
  }

  function rememberTrigger(trigger) {
    lastTrigger = isUsableTrigger(trigger) ? trigger : null;
  }

  function focusDocumentFallback() {
    var mainContent = document.getElementById('MainContent');
    if (isOnScreen(mainContent) && focusElement(mainContent, true)) return true;

    var bodyElement = document.body;
    if (!isRendered(bodyElement)) return false;
    var hadTabindex = bodyElement.hasAttribute('tabindex');
    var previousTabindex = bodyElement.getAttribute('tabindex');
    if (!hadTabindex) bodyElement.setAttribute('tabindex', '-1');

    var focused = focusElement(bodyElement, true);
    if (!hadTabindex) {
      bodyElement.removeAttribute('tabindex');
    } else if (previousTabindex !== null) {
      bodyElement.setAttribute('tabindex', previousTabindex);
    }
    return focused;
  }

  function restoreTriggerFocus() {
    var opener = lastTrigger;
    lastTrigger = null;

    if (isUsableTrigger(opener) && focusElement(opener, false)) return true;

    // The mobile account opener is now inside the closed navigation drawer.
    if (opener && opener.closest('.enigma-mobile-account-utility')) {
      var menuTrigger = document.getElementById('show-megamenu');
      if (isOnScreen(menuTrigger) && focusElement(menuTrigger, false)) return true;
    }

    var triggers = getTriggers().filter(function (trigger) {
      return trigger !== opener && isUsableTrigger(trigger);
    });
    if (focusFirstAvailable(triggers, false)) return true;
    return focusDocumentFallback();
  }

  function handleTransitionEnd(event) {
    if (event.target !== drawer || !isOpen()) return;
    if (drawer.contains(document.activeElement) && isRendered(document.activeElement)) return;
    focusInitialTarget(focusGeneration);
  }

  function setClassState(element, className, enabled) {
    if (!element) return;
    if (enabled) {
      element.classList.add(className);
    } else {
      element.classList.remove(className);
    }
  }

  function setLegacyAccountState(open, trigger) {
    if (!drawer) return;
    setClassState(drawer, 'act', open);
    getTriggers().forEach(function (accountTrigger) {
      setClassState(accountTrigger, 'act', open && accountTrigger === trigger);
    });

    setClassState(document.body, 'header_settings-open', open);
    var overlay = document.querySelector('.sidebar-overlay');
    var usesOverlay = trigger && trigger.getAttribute('data-overlay') === 'true';
    if (open && usesOverlay) {
      setClassState(overlay, 'act', true);
      if (overlay) overlay.setAttribute('data-close', 'header_settings');
    } else if (!open) {
      setClassState(overlay, 'act', false);
      if (overlay) overlay.removeAttribute('data-close');
    }
  }

  function reconcileTriggerClick(trigger, openBeforeClick) {
    if (!drawer) return;
    if (isOpen() === openBeforeClick) setLegacyAccountState(!openBeforeClick, trigger);
    syncState();
  }

  function reconcileCloseClick(openBeforeClick) {
    if (!drawer) return;
    if (openBeforeClick && isOpen()) setLegacyAccountState(false, null);
    syncState();
  }

  function syncState() {
    if (!drawer) return;
    var open = isOpen();

    drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
    if (open) {
      drawer.removeAttribute('inert');
    } else {
      drawer.setAttribute('inert', '');
    }
    enhanceTriggers(open);

    if (open && !wasOpen) beginInitialFocus();
    if (!open && wasOpen) {
      clearFocusSchedule();
      restoreTriggerFocus();
    }
    wasOpen = open;
  }

  function init() {
    var nextDrawer = document.querySelector(DRAWER_SELECTOR);
    var previousOpen = wasOpen || isOpen();

    if (nextDrawer && nextDrawer === drawer) {
      enhanceTriggers(isOpen());
      if (isOpen() && (!drawer.contains(document.activeElement) || !isRendered(document.activeElement))) {
        focusInitialTarget(focusGeneration);
      }
      return;
    }

    if (drawerObserver) drawerObserver.disconnect();
    if (drawer) drawer.removeEventListener('transitionend', handleTransitionEnd);
    clearFocusSchedule();
    drawer = nextDrawer;

    if (!drawer) {
      wasOpen = false;
      if (previousOpen) restoreTriggerFocus();
      return;
    }

    wasOpen = isOpen() ? false : previousOpen;
    syncState();
    drawer.addEventListener('transitionend', handleTransitionEnd);

    drawerObserver = new MutationObserver(syncState);
    drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  document.addEventListener('click', function (event) {
    var trigger = event.target && typeof event.target.closest === 'function'
      ? event.target.closest(TRIGGER_SELECTOR)
      : null;
    if (trigger) {
      var openBeforeTriggerClick = isOpen();
      rememberTrigger(trigger);
      if (trigger.closest('.enigma-mobile-account-utility') && window.novtheme &&
          typeof window.novtheme.closeMobileMenu === 'function') {
        window.novtheme.closeMobileMenu(false);
      }
      if (trigger.tagName !== 'BUTTON' && trigger.getAttribute('href') === '#') {
        event.preventDefault();
      }
      window.setTimeout(function () {
        reconcileTriggerClick(trigger, openBeforeTriggerClick);
      }, 0);
      return;
    }

    if (event.target && typeof event.target.closest === 'function'
      && (event.target.closest(CLOSE_SELECTOR) || event.target.closest('.sidebar-overlay'))) {
      var openBeforeCloseClick = isOpen();
      window.setTimeout(function () {
        reconcileCloseClick(openBeforeCloseClick);
      }, 0);
    }
  }, true);

  document.addEventListener('keydown', function (event) {
    var trigger = event.target && typeof event.target.closest === 'function'
      ? event.target.closest(TRIGGER_SELECTOR)
      : null;
    if (trigger && !isOpen() && trigger.tagName !== 'BUTTON' && event.key === ' ') {
      event.preventDefault();
      trigger.click();
      return;
    }

    if (!isOpen() || !drawer) return;

    if (event.key === 'Escape' || event.keyCode === 27) {
      var closeButton = drawer.querySelector(CLOSE_SELECTOR);
      if (!closeButton) return;
      event.preventDefault();
      closeButton.click();
      return;
    }

    if (event.key !== 'Tab' && event.keyCode !== 9) return;
    var focusable = getFocusableElements();
    if (focusable.length === 0) {
      event.preventDefault();
      focusElement(drawer, true);
      return;
    }

    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    var active = document.activeElement;

    if (event.shiftKey && (active === first || !drawer.contains(active))) {
      event.preventDefault();
      if (!focusFirstAvailable(focusable.slice().reverse(), false)) focusElement(drawer, true);
    } else if (!event.shiftKey && (active === last || !drawer.contains(active))) {
      event.preventDefault();
      if (!focusFirstAvailable(focusable, false)) focusElement(drawer, true);
    }
  }, true);

  document.addEventListener('shopify:section:load', init);

  window.EnigmaAccountDrawerA11y = {
    init: init,
    isOpen: isOpen,
    sync: syncState
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
